import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp } from "lucide-react";

/**
 * Simple spending chart / summary card.
 * Uses a bar visualization when daily data is available.
 */
export default function SpendingChart({ spending }) {
  if (!spending) {
    return (
      <Card>
        <CardContent className="p-5 text-center text-textSecondary">
          <TrendingUp className="w-8 h-8 mx-auto mb-2 text-textSecondary/30" />
          No spending data available
        </CardContent>
      </Card>
    );
  }

  const dailyData = spending.daily || [];
  const maxValue = Math.max(
    ...dailyData.map((d) => Number(d.total || d.amount || 0)),
    1
  );

  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="text-sm font-semibold text-textSecondary uppercase tracking-wider mb-3">
          30-Day Spending
        </h3>

        {dailyData.length > 0 ? (
          <div className="space-y-4">
            {/* Simple bar chart */}
            <div className="flex items-end gap-0.5 h-24">
              {dailyData.slice(-30).map((day, i) => {
                const value = Number(day.total || day.amount || 0);
                const height =
                  maxValue > 0
                    ? Math.max(2, (value / maxValue) * 100)
                    : 2;
                return (
                  <div
                    key={i}
                    className="flex-1 bg-primary/60 rounded-t hover:bg-primary transition-colors cursor-default"
                    style={{ height: `${height}%` }}
                    title={`${day.date || `Day ${i + 1}`}: ${value.toFixed(2)} credits`}
                  />
                );
              })}
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-textSecondary">Total</p>
                <p className="text-textPrimary font-medium">
                  {Number(spending.total_spent ?? 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </p>
              </div>
              <div>
                <p className="text-textSecondary">Daily Avg</p>
                <p className="text-textPrimary font-medium">
                  {Number(spending.daily_average ?? 0).toLocaleString(
                    undefined,
                    { minimumFractionDigits: 2 }
                  )}
                </p>
              </div>
              <div>
                <p className="text-textSecondary">Peak</p>
                <p className="text-textPrimary font-medium">
                  {maxValue.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-textSecondary">Total Spent</p>
                <p className="text-xl font-semibold text-textPrimary">
                  {Number(spending.total_spent ?? 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </p>
              </div>
              <div>
                <p className="text-textSecondary">Daily Average</p>
                <p className="text-xl font-semibold text-textPrimary">
                  {Number(spending.daily_average ?? 0).toLocaleString(
                    undefined,
                    { minimumFractionDigits: 2 }
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
